// Merge a parser-supplied contact (from a POD/HAWB form or email) into a
// location's saved contacts. ADD-ONLY: never overwrites a value the owner
// already has. Extracted from firestore.js so the rules are unit-testable.
//
// Rules:
//   - No name/phone/email parsed → nothing to do (null).
//   - A row matches when its phone is the same number modulo OCR drift
//     (10 digits within edit distance 1: 7045207770 vs 7045257770), or
//     its name looks like the same person (equal first names, last-name
//     prefixes sharing 3 chars: Sinozich vs Sinoalsh).
//   - Matched row → FILL any blank phone/email/name from the parse
//     (additive); if nothing was blank, no write at all.
//   - Same NAME but a clearly different number on both sides (not drift)
//     → that's NEW info (store line vs cell); append as its own row so
//     both numbers survive.
//   - No match at all → append as a new contact.
//
// Returns the new contacts array when anything changed, else null so
// callers can skip the write entirely (keeps owner-curated data
// untouched byte-for-byte).

function normPhone(p) {
  return String(p || "").replace(/\D/g, "");
}

// Edit distance — bails early at >1 since that's all we need.
function editDistanceOne(a, b) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0, j = 0, edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { i++; j++; continue; }
    edits++;
    if (edits > 1) return false;
    if (a.length === b.length) { i++; j++; }
    else if (a.length > b.length) i++;
    else j++;
  }
  if (i < a.length || j < b.length) edits++;
  return edits <= 1;
}

function nameLooksSame(a, b) {
  const norm = (s) => String(s || "").toLowerCase().trim().replace(/\s+/g, " ");
  const aP = norm(a).split(" ").filter(Boolean);
  const bP = norm(b).split(" ").filter(Boolean);
  if (!aP.length || !bP.length) return false;
  if (aP[0] !== bP[0]) return false; // first name must match
  const aLast = aP[aP.length - 1];
  const bLast = bP[bP.length - 1];
  if (aLast === aP[0] || bLast === bP[0]) return true; // single-name match
  return aLast.slice(0, 3) === bLast.slice(0, 3);
}

export function mergeParsedContact(existingContacts, stop) {
  const cName  = (stop?.contactName  || "").trim();
  const cPhone = (stop?.contactPhone || "").trim();
  const cEmail = (stop?.contactEmail || "").trim();
  if (!cName && !cPhone && !cEmail) return null;
  const arr = Array.isArray(existingContacts) ? existingContacts : [];
  const cPhoneDigits = normPhone(cPhone);

  let changed = false;
  let matchedRow = false;
  const next = arr.map((c) => {
    const cDigits = normPhone(c.phone);
    const phoneMatch = !!(cPhoneDigits && cDigits && editDistanceOne(cPhoneDigits, cDigits));
    const nameMatch = !!(cName && c.name && nameLooksSame(cName, c.name));
    if (!phoneMatch && !nameMatch) return c;
    // Same name but both sides carry CLEARLY different numbers — that's
    // a second number for the person/desk, not this row. Leave the row
    // alone; the append below keeps both numbers on file.
    if (nameMatch && !phoneMatch && cPhoneDigits && cDigits) return c;
    matchedRow = true;
    const fill = { ...c };
    if (cPhone && !c.phone) { fill.phone = cPhone; changed = true; }
    if (cEmail && !c.email) { fill.email = cEmail; changed = true; }
    if (cName && !c.name)   { fill.name  = cName;  changed = true; }
    return fill;
  });
  if (matchedRow) return changed ? next : null;
  return [...arr, { name: cName, phone: cPhone, email: cEmail }];
}
